import { Base } from '@studiometa/js-toolkit';
import { computeStoreOpeningStatus, formatOpeningWeek } from './StoreOpeningLogic.js';

/**
 * Rendu DOM et câblage du statut d'ouverture / dépliement « Voir horaires »
 * (chantier store-locator-revendeurs, point 3). Le calcul pur (heure de
 * Paris, statut, formatage) vit dans `StoreOpeningLogic.js`, testable sans
 * navigateur ; ce fichier-ci gère uniquement le HTML et les événements.
 *
 * Utilisé de deux façons dans le thème :
 * - liste de résultats (`archive-stores.php`) : `MapboxCluster.js` construit
 *   le bloc en chaîne pour chaque magasin puis appelle `initStoreOpeningBlock`
 *   après insertion dans le DOM (même style manuel que le reste du fichier) ;
 * - fiche magasin (`single-stores.php`) : composant `StoreOpeningStatus` ici,
 *   monté sur un seul bloc rendu par PHP.
 */

/**
 * Échappe une chaîne pour une insertion sûre dans du HTML (statut calculé
 * côté client : jamais de contenu venant du visiteur ici, mais on reste
 * défensif).
 * @param  {string} value
 * @returns {string}
 */
function escapeHtml(value) {
  return String(value).replace(/[&<>"']/g, (char) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  })[char]);
}

/**
 * Construit le HTML statique (avant hydratation) d'un bloc statut/horaires,
 * pour un magasin dont la semaine est reconnue, dont seul un texte non
 * reconnu existe, ou sans aucun texte (rien n'est rendu dans ce dernier cas).
 * `rawTextHtml` est déjà nettoyé côté serveur par `wp_kses_post` : jamais du
 * texte brut, jamais injecté dans un attribut HTML (voir critère 6 du
 * chantier) — il est ici placé comme contenu d'élément, pas comme attribut.
 * @param  {object|null} week
 * @param  {string|null} rawTextHtml
 * @param  {string} extraRootAttrs Attributs HTML additionnels pour l'élément
 *         racine `.store-opening` (ex. `data-store-index="3"` pour retrouver
 *         le bon bloc après un rendu en liste, où l'index dans le DOM ne
 *         correspond plus à l'index du magasin dès qu'un magasin n'a ni
 *         semaine reconnue ni texte, donc aucun bloc rendu).
 * @returns {string}
 */
export function renderStoreOpeningMarkup(week, rawTextHtml, extraRootAttrs = '') {
  if (!week && !rawTextHtml) {
    return '';
  }

  const now = new Date();
  const status = computeStoreOpeningStatus(week, now);

  const statusHtml = status
    ? `<p class="store-opening__status" data-ref="status" data-state="${status.state}">${escapeHtml(status.label)}</p>`
    : '';

  let daysHtml;
  if (week) {
    const rows = formatOpeningWeek(week, now)
      .map(
        (day) => `
        <li class="store-opening__day${day.isToday ? ' store-opening__day--today' : ''}">
          <span class="store-opening__day-label">${escapeHtml(day.label)}</span>
          <span class="store-opening__day-hours">${escapeHtml(day.hoursLabel)}</span>
        </li>`,
      )
      .join('');
    daysHtml = `<ul class="store-opening__days">${rows}</ul>`;
  } else {
    // Texte non reconnu : déjà nettoyé par wp_kses_post côté serveur, affiché
    // tel quel (peut contenir p/br/strong/b/em/i/span, jamais de script ni
    // d'attribut).
    daysHtml = `<div class="store-opening__text">${rawTextHtml}</div>`;
  }

  return `
    <div class="store-opening" data-ref="opening"${extraRootAttrs}>
      ${statusHtml}
      <button type="button" class="store-opening__toggle" data-ref="toggle" aria-expanded="false">
        Voir horaires
      </button>
      <div class="store-opening__panel" data-ref="panel" hidden>
        ${daysHtml}
      </div>
    </div>
  `;
}

/** Magasins actuellement affichés dont le statut doit se rafraîchir chaque minute. */
const trackedWeeks = new Map();
let refreshTimer = null;

function refreshTrackedStatuses() {
  const now = new Date();
  trackedWeeks.forEach((week, statusEl) => {
    if (!statusEl.isConnected) {
      trackedWeeks.delete(statusEl);
      return;
    }
    const status = computeStoreOpeningStatus(week, now);
    if (status) {
      statusEl.textContent = status.label;
      statusEl.dataset.state = status.state;
    }
  });
  // F5 (revue finale du chantier store-locator-revendeurs, 2026-09-24) :
  // ce rafraîchissement ne met à jour que le TEXTE de chaque statut déjà
  // affiché, jamais la liste elle-même — un magasin qui ferme à la minute
  // pile restait donc dans les résultats si le filtre « Ouvert maintenant »
  // était actif (critère 9), jusqu'à ce que le visiteur relance une action.
  // `StoreResultsList.js` écoute cet évènement et relance
  // `applyStoreFilters()` uniquement si ce filtre est actif (jamais de
  // re-rendu superflu pour les autres visiteurs).
  window.dispatchEvent(new CustomEvent('store-locator-minute-tick'));
}

function ensureRefreshTimer() {
  if (refreshTimer) {
    return;
  }
  // Rafraîchissement chaque minute, page ouverte, sans rechargement (H19).
  refreshTimer = setInterval(refreshTrackedStatuses, 60 * 1000);
}

/**
 * Câble le comportement (dépliement, rafraîchissement) d'un bloc rendu par
 * `renderStoreOpeningMarkup()` déjà inséré dans le DOM.
 * @param  {HTMLElement} rootEl Élément `.store-opening` (ou un ancêtre le contenant).
 * @param  {object|null} week
 */
export function initStoreOpeningBlock(rootEl, week) {
  if (!rootEl) {
    return;
  }
  const opening = rootEl.matches('.store-opening') ? rootEl : rootEl.querySelector('.store-opening');
  if (!opening) {
    return;
  }

  const toggle = opening.querySelector('[data-ref="toggle"]');
  const panel = opening.querySelector('[data-ref="panel"]');
  if (toggle && panel) {
    toggle.addEventListener('click', (event) => {
      // Le lien/bouton est hors de la zone `.list-item` comptée par le suivi
      // des clics (invariant R4/R13) : on évite en plus toute remontée vers
      // un ancêtre qui écouterait des clics par délégation.
      event.stopPropagation();
      const isOpen = panel.hasAttribute('hidden') === false;
      if (isOpen) {
        panel.setAttribute('hidden', '');
        toggle.textContent = 'Voir horaires';
        toggle.setAttribute('aria-expanded', 'false');
      } else {
        panel.removeAttribute('hidden');
        toggle.textContent = 'Masquer horaires';
        toggle.setAttribute('aria-expanded', 'true');
      }
    });
  }

  const statusEl = opening.querySelector('[data-ref="status"]');
  if (statusEl && week) {
    trackedWeeks.set(statusEl, week);
    ensureRefreshTimer();
  }
}

/**
 * Composant pour la fiche magasin (un seul bloc par page), monté sur
 * `data-component="StoreOpeningStatus"`. La semaine (si reconnue) arrive en
 * JSON sûr via `data-option-week` (uniquement jours/minutes, jamais de texte
 * libre). Le texte non reconnu, déjà nettoyé par `wp_kses_post`, est rendu
 * directement par PHP dans un `<template>` enfant : jamais dans un attribut.
 */
export default class StoreOpeningStatus extends Base {
  static config = {
    name: 'StoreOpeningStatus',
    options: {
      week: Object,
    },
  };

  mounted() {
    const week = this.$options.week && Object.keys(this.$options.week).length > 0 ? this.$options.week : null;
    const template = this.$el.querySelector('template[data-store-opening-text]');
    const rawTextHtml = template ? template.innerHTML : null;

    const markup = renderStoreOpeningMarkup(week, rawTextHtml);
    if (!markup) {
      this.$el.remove();
      return;
    }

    this.$el.innerHTML = markup;
    initStoreOpeningBlock(this.$el, week);
  }
}
